import { useEffect, useState, useMemo } from 'react';
import { CommonGrid, TopContainer } from '../layouts';
import { BigButton, TopButtonArea, ActionFilters, GlobalFilter } from '../common';
import _ from 'lodash';
import { icons } from '../assets';
import { setTitle, totalItemCount } from '../helpers/jsHelper';
import {
  apiArchiveAutomation,
  apiGetDownloadAutomation,
  apiRestoreMutipleArchivedAutomation,
  apiDeleteAutomation,
} from '../helpers/api';
import { useScreenSettings } from '../helpers/configureHelper';
import { useUserContext } from '../contexts/UserContext';
import { useFilterContext } from '../contexts/FilterContext';
import InfiniteScroll from '../components/InfiniteScroll/InfiniteScroll';
import { errorToast, multiInfoToast, successToast, warningToast } from '../helpers/toastHelper';
import {
  CARD_TYPE,
  COMMON_TAGS_MODEL_TYPES,
  MODEL_TYPES,
  MODULE_TYPES,
} from '../constants/constants';
import { ThumbnailContainerV2 } from '../common/ThumbnailContainerV2';
import AddToCollectionPopup from '../common/AddToCollectionPopup';
import { download } from '../common/DownloadFileController/downloadFile';
import { PageSearchBar } from '../common/UniversalSearchForm/UniversalSearchForm';
import PageTitle from '../common/PageTitle';
import { useAutomation } from '../hooks/useAutomation';
import RendersRestorePopup from '../common/RendersRestorePopup';
import BinIcon from '../components/icons/BinIcon';
import { useInfoDialogaActions } from '../hooks/useInfoDialogaActions';
import AutomationTemplateInfo from '../common/Automation/AutomationTemplateInfo';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isAdminOrOwner } from '../helpers/userHelper';
import { getMultipleResponseList } from '../helpers/urlHelper';
import { ADMIN_DASHBOARD } from '../constants/routePathConstants';
import { useStaticPermissions } from '../hooks/usePermissions';
import { PERMISSION } from '../constants/permissionConstants';
import { useSelector } from 'react-redux';
import { selectHasAutomationsData } from '../redux/slicers/subscriptionSlicer';
import FilterSideModalButton from '../common/FilterSideModalButton';
import TopContainerV2 from '../layouts/TopContainerV2';
import FooterOptions from "../layouts/FooterOptions";
import { GlobalDefaultTeamMemberFilter, GlobalSearchTagsFilter } from "../common/GlobalFilter";

const { CollectionIcon, DownloadIcon, ArchiveIcon } = icons;

export default function Automation({
  cardType = CARD_TYPE.AUTOMATION,
  title = 'Automation',
  useGlobalSearch,
  noDataText = 'Your automations live here!', 
  cardProps,
  designV2,
  hideStickyHeader = null
}) {
  const filterCtx = useFilterContext();
  const navigate = useNavigate();
  const { currentUser, isFreemiumUser } = useUserContext();
  const currentMemberId = currentUser.id;
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();

  const { hasPermission } = useStaticPermissions();

  const [showAutomationInfo, setShowAutomationInfo] = useState(null);

  const { sortOptions, filterOptions, defaultSortOption, showTeamMemberFilter } =
    useScreenSettings(cardType);

  const hasAutomationData = useSelector(selectHasAutomationsData);

  useEffect(() => {
    setTitle(title);
    filterCtx.setBreadcrumbs(null);
  }, []);

  const requestParams = useMemo(
    () => ({
      created_by: '',
    }),
    [currentMemberId]
  );

  const {
    list,
    loading,
    fetchMoreData,
    hasMore,
    total,
    hasData,
    anySelected,
    allSelected,
    selectedList,
    sortBy,
    selected,
    filterBy,
    onSortChange,
    refetchList,
    toggleAll,
    toggleItem,
    resetSelection,
    addToCollection,
    search,
    onSearchChange,
  } = useAutomation({
    requestParams,
    filterByKey: 'automation_type', // TODO: Change this
    defaultSort: defaultSortOption,
    useGlobalSearch,
    filterByOptions: filterOptions,
    modelType: MODEL_TYPES.AUTOMATION,
  });
  const isSingleAutomationSelected = selectedList.length === 1;

  async function handleDownloadClick(id) {
    setIsDownloading(true);
    const formData = new FormData();
    if (id) {
      formData.append('id', id);
    } else {
      selected.forEach((id) => {
        formData.append('id', id);
      });
    }
    const res = await apiGetDownloadAutomation(formData);
    if (res.status) {
      res?.data?.results?.forEach((item) => {
        download({ fileUrl: item.zip_url, fileName: `${item.name}.zip` });
      });
    } else {
      errorToast(res?.message);
    }
    setIsDownloading(false);
  }

  const deleteDialogTitle = isSingleAutomationSelected
    ? 'Are you sure you want to archive this automation?'
    : 'Delete selected?';
  const deleteDialogSubitle = isSingleAutomationSelected
    ? 'You can view the archived automation through filter panel.'
    : 'Automations having no or as disabled templates will be deleted & rest will be archived.';

  const handleDeleteClick = () => {
    const deletableAutomation = list
      .map((a) => {
        if (selected.includes(a.id)) {
          return a;
        }
      })
      .filter((a) => a)
      .filter((a) => !a.is_old_obj)
      .map((a) => a.id);

    if (!deletableAutomation.length) {
      warningToast("You can't delete an old automation file.");
      return;
    }

    openInfoDialog({
      title: deleteDialogTitle,
      description: deleteDialogSubitle,
      cancelButtonText: 'Cancel',
      cancelBtnColor: 'warning',
      okBtnColor: 'outline-dark',
      buttonText: isSingleAutomationSelected ? 'Archive' : 'Delete',
      onButtonClick: async () => {
        const onDeleteFailed = (failedItems) => {
          if (failedItems.length !== deletableAutomation.length) {
            refetchList();
          }
        };

        await apiArchiveAutomation({ idList: deletableAutomation, onDeleteFailed })
          .then((response) => {
            if (response) generateToast(response?.data);
          })
          .catch((error) => {
            if (!error?.status) {
              // errorToast(error?.data?.processing?.message);
              generateToast(error?.data);
            }
          })
          .finally(() => {
            closeInfoDialog();
            refetchList();
          });
      },
    });
  };

  const onInfoIconClick = (_, data) => {
    setShowAutomationInfo(data);
  };

  const onDeleteClick = async (_id) => {
    const automation = list.find((a) => a.id === _id);

    if (automation === -1) return;

    if (!isAdminOrOwner(currentUser) && automation?.member_details?.member_id !== currentMemberId) {
      warningToast('You do not have ownership to delete this item.', {
        toastId: `CAN_NOT_DELETE_${_id}`,
      });
      return;
    }

    const result = await apiArchiveAutomation({ idList: [_id] });

    generateToast(result?.data);
    refetchList();
  };

  const generateToast = (response) => {
    if (!response) return;

    const automationIds = [];
    Object.keys(response).forEach((a) => {
      if (!response[a].automation_list) return;
      automationIds.push(...response[a]?.automation_list);
    });

    const automationList = automationIds.map((a) => {
      return list.find((b) => b.id === a);
    });

    const updateResponse = {};
    Object.entries(response).forEach((a) => {
      if (Object.keys(a[1]).length > 0) {
        const val = {
          ...a[1],
          status: a[1].status_,
          action: a[1].message,
        };
        a[1].automation_list.forEach((b) => {
          updateResponse[b] = val;
        });
      }
    });

    multiInfoToast(getMultipleResponseList(updateResponse, automationList), `Delete Details`);
  };

  if (!hasAutomationData && !hasPermission(PERMISSION.CAN_CREATE_AUTOMATION)) {
    // if(isFreemiumUser()) {
    navigate(ADMIN_DASHBOARD);
    return null;
  }

  return (
    <CommonGrid
      sticky={hideStickyHeader ? false : true}
      noDataText={search ? `You don't have any ${cardType} with "${search}" name.` : noDataText}
      className={(anySelected || allSelected) && designV2 ? 'show-action-footer' : ''}
      {...(designV2
        ? {
            topContainer: (
            <TopContainerV2
            subClassName="myFilesFilterContainer"
                  globalFilter={
                    !useGlobalSearch && (
                      <div>
                        <GlobalFilter designV2 />
                    <GlobalSearchTagsFilter designV2={designV2} />
                    <GlobalDefaultTeamMemberFilter designV2 />
                      </div>
                    )
                  }>
              <>
                {!useGlobalSearch && (
                  <>
                  <div className="totalCount">{totalItemCount(total)}</div>
                <PageSearchBar
                    designV2
                    model={COMMON_TAGS_MODEL_TYPES.AUTOMATION}
                    onSearchChange={onSearchChange}
                    search={search}
                    hideTags={true}
                  />
                  <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                      <ActionFilters
                        hasData={hasData}
                        loading={loading}
                        sortProps={{
                          value: sortBy,
                          data: sortOptions,
                          onChange: onSortChange,
                          containerClass: 'top-filter',
                        }}
                        showTeamMemberFilter={false}
                      />
                    </>
                  )}
                </>
              </TopContainerV2>
            ),
          }
        : {
            topContainer: (
              <TopContainer
                title={
                  !useGlobalSearch && <PageTitle title={title} total={total} search={search} />
                }
                item={cardType}
                hasData={hasData}
                buttonArea={
                  <div style={{ display: 'flex' }}>
                    {!useGlobalSearch && (
                      <TopButtonArea
                        hasData={hasData}
                        item={cardType}
                        topButtonList={
                          searchParams.get('is_archive')
                            ? [
                                {
                                  element: RendersRestorePopup,
                                  bigButton: true,
                                  bigBtnClass: 'lf-btn-3',
                                  item: cardType,
                                  onSuccess: refetchList,
                                  actionWord: 'restore',
                                  deleteAction: apiRestoreMutipleArchivedAutomation,
                                  title: 'Are you sure you want to restore this automation?',
                                },
                              ]
                            : [
                                {
                                  element: BigButton,
                                  iconBefore: isSingleAutomationSelected ? (
                                    <ArchiveIcon />
                                  ) : (
                                    <BinIcon />
                                  ),
                                  bigButton: true,
                                  buttonTitle: isSingleAutomationSelected ? 'Archive' : 'Delete',
                                  bigBtnClass: 'lf-btn-3',
                                  onClick: handleDeleteClick,
                                },
                                {
                                  element: BigButton,
                                  bigBtnClass: 'lf-btn-2',
                                  iconBefore: <CollectionIcon />,
                                  buttonTitle: 'Add To Collection',
                                  onClick: addToCollection,
                                },
                                // {
                                //   element: BigButton,
                                //   bigBtnClass: 'lf-btn-1',
                                //   isLoading: isDownloading,
                                //   buttonTitle: 'Download',
                                //   iconBefore: <DownloadIcon />,
                                //   onClick: handleDownloadClick,
                                // },
                              ]
                        }
                        anySelected={anySelected}
                        selectedList={selectedList}
                      />
                    )}
                    {!useGlobalSearch && (
                      <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                    )}
                  </div>
                }
                searchBar={
                  !useGlobalSearch && (
                    <div className="mt-2">
                      <PageSearchBar
                        model={COMMON_TAGS_MODEL_TYPES.AUTOMATION}
                        onSearchChange={onSearchChange}
                        search={search}
                        hideTags={true}
                      />
                    </div>
                  )
                }
              />
            ),
          })}
      item={cardType}
      hasData={list.length > 0 || loading}
      list={list}
      {...(!designV2 &&
        !useGlobalSearch && {
          filterArea: (
            <>
              <ActionFilters
                hasData={hasData}
                loading={loading}
                sortProps={{
                  value: sortBy,
                  data: sortOptions,
                  onChange: onSortChange,
                }}
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
                showTeamMemberFilter={false}
              />
              <GlobalFilter />
            </>
          ),
        })}>
      <InfiniteScroll
        dataLength={list.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loading={loading}
        hasChildren={hasData}
        scrollableTarget="infiniteScroll">
        <ThumbnailContainerV2
          designV2={designV2}
          items={list}
          modelType={MODEL_TYPES.AUTOMATION}
          selected={selected}
          cardProps={{
            fullWidth: true,
            onSelect: toggleItem,
            // onEdit: onEditClick,
            onEditInfo: onInfoIconClick,
            onDelete: onDeleteClick,
            ...cardProps,
          }}
        />
        {hasMore && <div id="whiteShadow" />}
      </InfiniteScroll>

      {(anySelected || allSelected) && designV2 && (
        <FooterOptions>
          <>
            <div className="selected-item">
              <ActionFilters
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
              />
              <span>{selectedList?.length || 0} Selected</span>
            </div>
            <div className="footer-action">
           
                <TopButtonArea
                  hasData={hasData}
                  item={cardType}
                  topButtonList={
                    searchParams.get('is_archive')
                      ? [
                          {
                            element: RendersRestorePopup,
                            bigButton: true,
                            bigBtnClass: 'lf-btn-3',
                            item: cardType,
                            onSuccess: refetchList,
                            actionWord: 'restore',
                            deleteAction: apiRestoreMutipleArchivedAutomation,
                            title: 'Are you sure you want to restore this automation?',
                          },
                        ]
                      : [
                          {
                            element: BigButton,
                            iconBefore: isSingleAutomationSelected ? <ArchiveIcon /> : <BinIcon />,
                            bigButton: true,
                            buttonTitle: isSingleAutomationSelected ? 'Archive' : 'Delete',
                            bigBtnClass: 'lf-btn-3',
                            onClick: handleDeleteClick,
                          },
                          {
                            element: BigButton,
                            bigBtnClass: 'lf-btn-2',
                            iconBefore: <CollectionIcon />,
                            buttonTitle: 'Add To Collection',
                            onClick: addToCollection,
                          },
                          // {
                          //   element: BigButton,
                          //   bigBtnClass: 'lf-btn-1',
                          //   isLoading: isDownloading,
                          //   buttonTitle: 'Download',
                          //   iconBefore: <DownloadIcon />,
                          //   onClick: handleDownloadClick,
                          // },
                        ]
                  }
                  anySelected={anySelected}
                  selectedList={selectedList}
                />
             
            </div>
          </>
        </FooterOptions>
      )}

      <AddToCollectionPopup onSuccess={resetSelection} />
      <AutomationTemplateInfo
        automationData={showAutomationInfo}
        show={!!showAutomationInfo}
        setShow={() => setShowAutomationInfo(null)}
        title={showAutomationInfo?.name}
        isTemplateInformation={false}
      />
    </CommonGrid>
  );
}
